/**
 * FlashcardReview Entity
 * Represents a student's SM-2 review progress for a single flashcard
 *
 * DB Table: FlashcardReviews
 * - id (PK)
 * - flashcard_id (FK)
 * - student_id (FK to users)
 * - easiness_factor (SM-2 EF, default 2.5)
 * - interval_days
 * - repetitions
 * - next_review_date
 * - last_quality (0-5)
 * - last_reviewed_at
 * - total_reviews
 * - UNIQUE(flashcard_id, student_id)
 */
export interface FlashcardReviewProps {
  id: string;
  flashcardId: string;
  studentId: string;
  easinessFactor: number;
  intervalDays: number;
  repetitions: number;
  nextReviewDate: Date;
  lastQuality: number | null;
  lastReviewedAt: Date | null;
  totalReviews: number;
}

export class FlashcardReview {
  private readonly props: FlashcardReviewProps;

  constructor(props: FlashcardReviewProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get flashcardId(): string {
    return this.props.flashcardId;
  }

  get studentId(): string {
    return this.props.studentId;
  }

  get easinessFactor(): number {
    return this.props.easinessFactor;
  }

  get intervalDays(): number {
    return this.props.intervalDays;
  }

  get repetitions(): number {
    return this.props.repetitions;
  }

  get nextReviewDate(): Date {
    return this.props.nextReviewDate;
  }

  get lastQuality(): number | null {
    return this.props.lastQuality;
  }

  get lastReviewedAt(): Date | null {
    return this.props.lastReviewedAt;
  }

  get totalReviews(): number {
    return this.props.totalReviews;
  }

  isDue(): boolean {
    return new Date() >= this.props.nextReviewDate;
  }

  /**
   * A card is "mastered" if the interval is >= 21 days (3 weeks)
   */
  isMastered(): boolean {
    return this.props.intervalDays >= 21;
  }

  isNew(): boolean {
    return this.props.totalReviews === 0;
  }

  getDaysUntilDue(): number {
    const now = new Date();
    const diff = this.props.nextReviewDate.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  toPlainObject(): FlashcardReviewProps {
    return { ...this.props };
  }
}
